import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
} from '@mui/material';

/**
 * GoalStatusFilter - A multi-select filter for goal statuses
 *
 * @param {Array} selectedStatuses - Array of selected status objects [{id, name, color}]
 * @param {Function} onStatusChange - Callback when selection changes (newSelectedStatuses) => void
 * @param {Array} statuses - Array of all available statuses [{id, name, color}]
 * @param {string} label - Label for the filter
 * @param {number} width - Width of the filter
 */
const GoalStatusFilter = ({
  selectedStatuses = [],
  onStatusChange,
  statuses = [],
  label = 'Status',
  width = 250,
}) => {
  const handleChange = (event) => {
    const selectedIds = event.target.value;
    const newSelectedStatuses = statuses.filter(s => selectedIds.includes(s.id));
    onStatusChange(newSelectedStatuses);
  };

  const selectedIds = selectedStatuses.map(s => s.id);

  return (
    <FormControl sx={{ width }}>
      <InputLabel
        id="goal-status-filter-label"
        size="small"
        sx={{
          backgroundColor: 'white',
          px: 0.5,
        }}
      >
        {label}
      </InputLabel>
      <Select
        labelId="goal-status-filter-label"
        multiple
        value={selectedIds}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return <span style={{ color: '#9ca3af' }}>All Statuses</span>;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((statusId) => {
                const status = statuses.find(s => s.id === statusId);
                return (
                  <Chip
                    key={statusId}
                    label={status?.name || 'Unknown'}
                    size="small"
                    sx={{
                      height: 22,
                      backgroundColor: status?.color || '#6b7280',
                      color: 'white',
                      '& .MuiChip-label': {
                        px: 1,
                        fontSize: '12px',
                      },
                    }}
                  />
                );
              })}
            </Box>
          );
        }}
        size="small"
        sx={{
          backgroundColor: 'white',
          minHeight: '40px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E2E8F0',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563eb',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563eb',
          },
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
      >
        {statuses.map((status) => (
          <MenuItem key={status.id} value={status.id}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: status.color || '#6b7280',
                mr: 1,
                flexShrink: 0,
              }}
            />
            {status.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default GoalStatusFilter;
